import {
  loaderCacheKey,
  mergeLoaderCache,
  readLoaderCache,
  writeLoaderCache,
} from "./rill-loader-cache";

export const CONFIGS_BY_CLIENT_ID_LOADER = "configsByClientId";

// Cache-busting tags (see js/middleware/cache-buster.js): an entry is dropped
// when msb-api bumps any of these in the viewer's last-modified doc. Shared
// with the `configsByClientId` / `sectionBlocksByClientId` loaders in
// js/load/client-configuration.js so the two can't drift.
//
// Has-data flags (batchDetectSectionsWithData in msb-api) read exercises,
// nutrition plans, meal plans, goals and the client user. "cycle" is on the
// list because some program writes create or delete a client's exercises but
// only bump "cycle" (cycle-push, cycle-delete-single-workout,
// cycle-check-teams-update) — without it, pushing a program to a client
// left them under "Needs attention" until the 2h TTL.
export const SECTION_HAS_DATA_CACHE_META = [
  "exercise",
  "cycle",
  "nutrition-plan",
  "meal-plan",
  "client-goals",
  "user",
];

// Effective configs: the config docs, plus the coach's templates — a linked
// template's sections, and the coach default an unassigned client resolves
// to (setting a default or deleting a template only bumps
// "client-configuration-templates"). A client with no doc and no default gets
// a config synthesized from live data, hence the has-data tags too.
export const CONFIGS_CACHE_META = [
  "client-configurations",
  "client-configuration-templates",
  ...SECTION_HAS_DATA_CACHE_META,
];

export const CONFIGS_BY_CLIENT_ID_CACHE_OPTS = {
  ttl: "2 hours",
  meta: ["no-bust", ...CONFIGS_CACHE_META],
};

/**
 * One accumulating map per session, shared by every view of the athletes
 * list ("All clients", a group, "All organization", another coach's list).
 * A client's effective config doesn't depend on which view loaded it — the
 * viewer is the same and the API resolves it against the client's owning
 * coach — so a config loaded in one view is reused by every other view
 * instead of being fetched again. Busting (CONFIGS_BY_CLIENT_ID_CACHE_OPTS
 * meta) still drops it on any relevant write.
 */
const SHARED_CONFIGS_CACHE_ARGS = { scope: "shared" };

export const readConfigsByClientIdCache = (session) =>
  readLoaderCache(
    session,
    CONFIGS_BY_CLIENT_ID_LOADER,
    SHARED_CONFIGS_CACHE_ARGS
  ) ?? {};

export const mergeConfigsByClientIdCache = (session, batch) => {
  if (!batch || !Object.keys(batch).length) return null;
  return mergeLoaderCache(
    session,
    CONFIGS_BY_CLIENT_ID_LOADER,
    batch,
    CONFIGS_BY_CLIENT_ID_CACHE_OPTS,
    SHARED_CONFIGS_CACHE_ARGS
  );
};

/**
 * Cached configs for `clientIds`, trimmed to what msb-api's config-filters.js
 * reads (template + section modes + enabled trackers), to send as the filter
 * endpoint's `knownConfigs` — the API then only builds the configs that
 * aren't cached. Keep the fields in sync with `summarizeConfigForFilters`.
 */
export const knownConfigsForFilter = (session, clientIds) => {
  const cached = readConfigsByClientIdCache(session);
  const known = {};
  for (const id of clientIds) {
    const config = cached[id];
    if (!config?.sections) continue;
    const sections = {};
    for (const [key, section] of Object.entries(config.sections)) {
      if (!section || typeof section !== "object") continue;
      sections[key] = { mode: section.mode, clientOptIn: section.clientOptIn };
    }
    const items = config.sections.tracking?.items;
    if (items && sections.tracking) {
      sections.tracking.items = Object.fromEntries(
        Object.entries(items).map(([key, item]) => [
          key,
          {
            coachRequired: item?.coachRequired,
            clientEnabled: item?.clientEnabled,
          },
        ])
      );
    }
    known[id] = {
      templateId: config.templateId,
      templateType: config.templateType,
      templateName: config.templateName,
      sections,
    };
  }
  return known;
};

export const pickConfigsForClientIds = (configsByClientId, clientIds) =>
  Object.fromEntries(
    clientIds
      .filter((id) => configsByClientId?.[id])
      .map((id) => [id, configsByClientId[id]])
  );

// Filter responses (POST /actions/client-configuration/filter), one entry per
// list scope + filter combination. A summary depends on the effective config,
// the has-data flags and the coach's published pull widgets
// (needs-attention), so it's busted by all of those writes.
export const CONFIG_LIST_FILTER_LOADER = "configListFilter";
export const CONFIG_LIST_FILTER_CACHE_META = [...CONFIGS_CACHE_META, "widget"];
const CONFIG_LIST_FILTER_CACHE_OPTS = {
  ttl: CONFIGS_BY_CLIENT_ID_CACHE_OPTS.ttl,
  meta: ["no-bust", ...CONFIG_LIST_FILTER_CACHE_META],
};

/**
 * Cache key args for a filter request. Built the same way by the action (from
 * the cast request body) and the browser, so both address one entry — group /
 * coach go through String() because the body cast turns e.g. a group named
 * "2024" into a number.
 */
export const configListFilterCacheArgs = ({
  clientIds,
  filterGroup,
  organizationCoach,
  filters,
  match,
}) => ({
  clientIds,
  filterGroup: filterGroup == null ? undefined : String(filterGroup),
  organizationCoach:
    organizationCoach == null ? undefined : String(organizationCoach),
  filters,
  match,
});

// Session size: the whole session is one document in the session store
// (Keyv on MongoDB, 16 MB per document) and travels to the browser on every
// page load and back on every in-app navigation. A full filter response
// carries a summary + has-data flags for every client (~450 B per client, so
// ~900 KB per filter combination at 2000 clients), and each combination was
// its own entry. Per-client data already lives once in the shared maps below
// (rememberFilterSummaries), so a combination only keeps what's specific to
// it — matchingIds + counts — under a short key (client ids hashed, not
// listed), and only the most recent MAX_CACHED_FILTER_COMBOS of them.
const MAX_CACHED_FILTER_COMBOS = 10;
const CONFIG_LIST_FILTER_INDEX_LOADER = "configListFilterIndex";

/** FNV-1a, two seeds → 16 hex chars. Same result on server and browser. */
const hashIds = (ids) => {
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ ids.length;
  for (const id of ids) {
    const str = `${id},`;
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i);
      a = Math.imul(a ^ code, 0x01000193);
      b = Math.imul(b ^ code, 0x5bd1e995);
    }
  }
  return (
    (a >>> 0).toString(16).padStart(8, "0") +
    (b >>> 0).toString(16).padStart(8, "0")
  );
};

/** Short session key args for a filter request's cache entry. */
export const configListFilterCacheKeyArgs = ({ clientIds, ...rest }) => ({
  ...rest,
  clientCount: clientIds?.length ?? 0,
  clientIdsHash: hashIds(clientIds ?? []),
});

/** Only what's specific to one filter combination (see above). */
const slimFilterResponse = (data) => ({
  matchingIds: data?.matchingIds ?? [],
  counts: data?.counts ?? null,
});

export const readConfigListFilterCache = (session, cacheArgs) =>
  readLoaderCache(
    session,
    CONFIG_LIST_FILTER_LOADER,
    configListFilterCacheKeyArgs(cacheArgs)
  );

export const writeConfigListFilterCache = (session, cacheArgs, data) => {
  const keyArgs = configListFilterCacheKeyArgs(cacheArgs);
  writeLoaderCache(
    session,
    CONFIG_LIST_FILTER_LOADER,
    slimFilterResponse(data),
    CONFIG_LIST_FILTER_CACHE_OPTS,
    keyArgs
  );
  // Most recently written last; drop the oldest past the cap. The index is
  // busted with the entries (same tags), and deleting an entry that already
  // expired is a no-op.
  const key = loaderCacheKey(CONFIG_LIST_FILTER_LOADER, keyArgs);
  const index = readLoaderCache(session, CONFIG_LIST_FILTER_INDEX_LOADER);
  const keys = [
    ...(Array.isArray(index) ? index : []).filter(
      (k) => k !== key && session.has(k)
    ),
    key,
  ];
  while (keys.length > MAX_CACHED_FILTER_COMBOS) session.delete(keys.shift());
  writeLoaderCache(
    session,
    CONFIG_LIST_FILTER_INDEX_LOADER,
    keys,
    CONFIG_LIST_FILTER_CACHE_OPTS
  );
};

export const SECTION_BLOCKS_CACHE_OPTS = {
  ttl: "2 hours",
  meta: ["no-bust", ...SECTION_HAS_DATA_CACHE_META],
};

export const writeSectionBlocksCache = (session, sectionBlocks, clientIds) =>
  writeLoaderCache(
    session,
    "sectionBlocksByClientId",
    sectionBlocks ?? {},
    SECTION_BLOCKS_CACHE_OPTS,
    { clientIds }
  );

// ---------------------------------------------------------------------------
// Browser session bridge. The browser keeps its own copy of the session
// (@rill/session), loaded once on page load and POSTed back — replacing the
// server's copy — after every in-app navigation. So data the list fetches
// through /actions/* (cached in the *server* session there) is invisible to
// controllers running in the browser (e.g. on a group switch) and is wiped
// by the next sync. Writing it into the browser copy too fixes both: the
// browser controller sees it, and the next sync carries it to the server.
// ---------------------------------------------------------------------------

let browserSession = null;

/** Called by the athletes controller when it runs in the browser. */
export const rememberBrowserSession = (session) => {
  if (process.browser) browserSession = session;
};

/** Lazily-loaded configs → browser session (no-op on the server). */
export const rememberLoadedConfigs = (batch) => {
  if (browserSession) mergeConfigsByClientIdCache(browserSession, batch);
};

/** Prefetched has-data flags → browser session (no-op on the server). */
export const rememberSectionBlocks = (sectionBlocks, clientIds) => {
  if (browserSession) {
    writeSectionBlocksCache(browserSession, sectionBlocks, clientIds);
  }
};

/**
 * Filter response → browser session (no-op on the server). The action caches
 * it in the server session, but the next browser sync replaces that copy, so
 * without this the cache never survived a group switch.
 */
export const rememberConfigListFilter = (cacheArgs, data) => {
  if (browserSession) {
    writeConfigListFilterCache(
      browserSession,
      configListFilterCacheArgs(cacheArgs),
      data
    );
  }
};

// Per-client filter summaries + has-data flags from filter responses, one
// accumulating map each shared by every view (like the configs above). Once
// it covers every client of a view, the list matches that view locally
// instead of asking again (see matchFiltersLocally in configFilters.ts).
// Same busting as the filter responses — a summary goes stale on the same
// writes.
const FILTER_SUMMARIES_LOADER = "configListFilterSummaries";
const FILTER_SECTION_BLOCKS_LOADER = "configListFilterSectionBlocks";

export const readFilterSummaries = () =>
  (browserSession &&
    readLoaderCache(
      browserSession,
      FILTER_SUMMARIES_LOADER,
      SHARED_CONFIGS_CACHE_ARGS
    )) ??
  {};

export const readFilterSectionBlocks = () =>
  (browserSession &&
    readLoaderCache(
      browserSession,
      FILTER_SECTION_BLOCKS_LOADER,
      SHARED_CONFIGS_CACHE_ARGS
    )) ??
  {};

/** Filter response summaries / has-data flags → browser session. */
export const rememberFilterSummaries = (summaries, sectionBlocks) => {
  if (!browserSession) return;
  for (const [name, patch] of [
    [FILTER_SUMMARIES_LOADER, summaries],
    [FILTER_SECTION_BLOCKS_LOADER, sectionBlocks],
  ]) {
    if (!patch || !Object.keys(patch).length) continue;
    mergeLoaderCache(
      browserSession,
      name,
      patch,
      CONFIG_LIST_FILTER_CACHE_OPTS,
      SHARED_CONFIGS_CACHE_ARGS
    );
  }
};
